/**
 * ReservationActionDialogs — all confirmation/form dialogs for reservation actions.
 *
 * Dialogs:
 *  - ConfirmDialog: generic confirm for upgrade_hard, close_sale, force_release
 *  - CancelDialog: confirm cancel + shows what becomes free (`reservation-cancel-dialog.tsx`)
 *  - EditDatesDialog, OverrideStatusDialog, EditExpiryDeadlineDialog (`reservation-action-dialogs-parts.tsx`)
 *
 * Props-only component with no data fetching.
 */

import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { CancelDialog } from './reservation-cancel-dialog';
import {
  EditDatesDialog,
  OverrideStatusDialog,
  EditExpiryDeadlineDialog,
} from './reservation-action-dialogs-parts';
import { guardClose } from './reservation-dialog-types';
import type { ActiveDialog, ReservationDialogLabels } from './reservation-dialog-types';

export { guardClose };
export type { ActiveDialog, ReservationDialogLabels };

export interface ReservationActionDialogsProps {
  activeDialog: ActiveDialog;
  reservationId: number;
  /** Needed to build the `POST /reservations/batch-release` `cells` payload for
   * CancelDialog's "release only part" choice (CU-86cbh03pj parity). */
  mediaUnitId: number;
  mediaUnitName: string;
  dateFrom: string;
  dateTo: string;
  isAdmin: boolean;
  labels: ReservationDialogLabels;
  /** True while the dispatched mutation is in flight — disables confirm AND cancel/close buttons. */
  isPending?: boolean;
  /**
   * The row's current effective expiry (ISO instant string), shown as context
   * in the expiry-deadline edit dialog (CU-86cb7qgc9). Null when unknown —
   * the dialog omits the "current deadline" line rather than guessing.
   */
  currentDeadline?: string | null;
  onClose: () => void;
  onConfirmAction: (action: string, data?: Record<string, unknown>) => void;
}

// ─── ConfirmDialog ───────────────────────────────────────────

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  isPending = false,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  isPending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{title}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={isPending}>
            {confirmLabel}
          </Button>
        </div>
      }
      onClose={guardClose(onClose, isPending)}
    >
      <p className="text-sm text-muted-foreground">{body}</p>
    </ModalDialog>
  );
}

// eslint-disable-next-line complexity -- one branch per dialog type (7 now, was 6); each branch is a single early return
export function ReservationActionDialogs({
  activeDialog,
  reservationId,
  mediaUnitId,
  mediaUnitName,
  dateFrom,
  dateTo,
  isAdmin,
  labels,
  isPending = false,
  currentDeadline = null,
  onClose,
  onConfirmAction,
}: ReservationActionDialogsProps) {
  if (activeDialog === null) return null;

  if (activeDialog === 'upgrade_hard') {
    return (
      <ConfirmDialog
        title={labels.upgradeHardTitle}
        body={labels.upgradeHardBody}
        confirmLabel={labels.confirmLabel}
        cancelLabel={labels.cancelBtnLabel}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={() => onConfirmAction('upgrade_hard')}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'close_sale') {
    return (
      <ConfirmDialog
        title={labels.closeSaleTitle}
        body={labels.closeSaleBody}
        confirmLabel={labels.confirmLabel}
        cancelLabel={labels.cancelBtnLabel}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={() => onConfirmAction('close_sale')}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'force_release') {
    return (
      <ConfirmDialog
        title={labels.forceReleaseTitle}
        body={labels.forceReleaseBody}
        confirmLabel={labels.confirmLabel}
        cancelLabel={labels.cancelBtnLabel}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={() => onConfirmAction('force_release')}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'cancel') {
    return (
      <CancelDialog
        reservationId={reservationId}
        mediaUnitId={mediaUnitId}
        mediaUnitName={mediaUnitName}
        dateFrom={dateFrom}
        dateTo={dateTo}
        labels={labels}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={() => onConfirmAction('cancel')}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'edit_dates') {
    return (
      <EditDatesDialog
        initialDateFrom={dateFrom}
        initialDateTo={dateTo}
        isAdmin={isAdmin}
        labels={labels}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={(data) => onConfirmAction('edit_dates', data)}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'override_status') {
    return (
      <OverrideStatusDialog
        labels={labels}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={(data) => onConfirmAction('override_status', data)}
        onClose={onClose}
      />
    );
  }

  if (activeDialog === 'expiry_deadline') {
    return (
      <EditExpiryDeadlineDialog
        currentDeadline={currentDeadline}
        labels={labels}
        isPending={isPending}
        // eslint-disable-next-line i18next/no-literal-string -- action discriminator, not user-facing
        onConfirm={(data) => onConfirmAction('expiry_deadline', data)}
        onClose={onClose}
      />
    );
  }

  return null;
}
